import type { OfficialResult } from '../types';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';

const API_BASE_URL = 'https://bikedata.club';

export interface BikeDataEvent {
    id: string;
    name: string;
    date: string;
    state?: string;
}

/**
 * Service wrapper for the bikedata.club API
 */
export const bikedataService = {
    /**
     * Searches for USAC events.
     * Useful for linking a RacelineIQ RaceEvent to a bikedata.club Event ID.
     */
    searchEvents: async (params: { query?: string; state?: string; year?: number }): Promise<BikeDataEvent[]> => {
        try {
            const queryParams = new URLSearchParams();
            
            if (params.year) {
                queryParams.append('from_date', `${params.year}-01-01`);
                queryParams.append('to_date', `${params.year}-12-31`);
            }
            
            const response = await fetch(`${API_BASE_URL}/usac/events?${queryParams.toString()}`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch events: ${response.statusText}`);
            }
            
            const data = await response.json();
            
            // Map the raw JSON response to our BikeDataEvent interface
            let events: BikeDataEvent[] = (data.events || []).map((e: any) => ({
                id: e.id.toString(),
                name: e.name,
                date: e.start_date,
                state: e.location || ''
            }));

            // Client-side filtering (bikedata.club API doesn't support text search natively)
            if (params.query) {
                events = events.filter(e => e.name.toLowerCase().includes(params.query!.toLowerCase()));
            }
            if (params.state) {
                events = events.filter(e => e.state?.toLowerCase().includes(params.state!.toLowerCase()));
            }

            return events;
        } catch (error) {
            console.error("bikedataService.searchEvents error:", error);
            return [];
        }
    },

    /**
     * Fetches official results for a specific event and category.
     */
    getResults: async (eventId: string, categoryId: string): Promise<OfficialResult[]> => {
        try {
            // This function now handles caching on the backend.
            const getAndCacheFn = httpsCallable(functions, 'getAndCacheOfficialResults');
            const response = await getAndCacheFn({ raceId: eventId, categoryId: categoryId });
            
            const payload = response.data as { results: OfficialResult[] };

            // The data is already mapped to OfficialResult[] on the backend.
            return payload.results || [];
        } catch (error) {
            console.error("bikedataService.getResults error:", error);
            return [];
        }
    },

    /**
     * Looks up a rider's profile by USAC license number to verify identity.
     */
    getRiderByLicense: async (licenseNumber: string) => {
        try {
            // We pass ?year=none to skip the heavy join and just get the license metadata
            const response = await fetch(`${API_BASE_URL}/usac/athletes/${encodeURIComponent(licenseNumber)}?year=none`);
            if (!response.ok) return null;
            
            return await response.json();
        } catch (error) {
            console.error("bikedataService.getRiderByLicense error:", error);
            return null;
        }
    }
};
